import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import Footer from '../components/Footer';
import BlogBody from '../components/blog/BlogBody';
import BlogGrid from '../components/blog/BlogGrid';
import BlogCard from '../components/blog/BlogCard';
import BlogThumbnail from '../components/blog/BlogThumbnail';
import BlogPostCta from '../components/blog/BlogPostCta';
import NotFound from './NotFound';
import { InnerCard, InnerContainer, InnerSection } from '../components/InnerPageLayout';
import usePageMeta, { SITE_ORIGIN } from '../hooks/usePageMeta';
import { getPostBySlug, getRelatedPosts } from '../data/blog';
import { BlogPost as BlogPostData } from '../types/blogTypes';
import { formatPostDate } from '../utils/blogFormat';

const SHARE_IMAGE = `${SITE_ORIGIN}/og-chot-buddies.jpg`;

const Wrap = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding-top: 120px;
`;

const BackLink = styled(Link)`
  display: inline-block;
  margin-bottom: 20px;
  font-size: 15px;
  font-weight: 500;
`;

const ArticleHeader = styled.header`
  margin-bottom: 36px;
  display: grid;
  gap: 18px;
`;

const ByLine = styled.p`
  margin: 0;
  font-size: 15px;
  color: #3f4c5a;

  strong {
    color: #1f2b3a;
    font-weight: 600;
  }
`;

const ArticleCard = styled(InnerCard)`
  @media (max-width: 600px) {
    padding: 24px 18px;
  }
`;

const RelatedTitle = styled.h2`
  margin: 64px 0 24px;
  text-align: center;
`;

const buildJsonLd = (post: BlogPostData): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BlogPosting',
  headline: post.title,
  description: post.description,
  datePublished: post.publishedAt,
  dateModified: post.publishedAt,
  author: { '@type': 'Organization', name: post.author },
  publisher: { '@type': 'Organization', name: 'Chots AI', url: SITE_ORIGIN },
  mainEntityOfPage: `${SITE_ORIGIN}/blog/${post.slug}/`,
  image: SHARE_IMAGE,
  keywords: post.keywords.join(', '),
});

const BlogPostView: React.FC<{ post: BlogPostData }> = ({ post }) => {
  usePageMeta({
    title: `${post.title} | Chots AI`,
    description: post.description,
    path: `/blog/${post.slug}/`,
    image: SHARE_IMAGE,
    ogType: 'article',
    jsonLd: buildJsonLd(post),
  });

  const related = getRelatedPosts(post);

  return (
    <>
      <InnerSection>
        <InnerContainer>
          <Wrap>
            <BackLink to="/blog/">← All posts</BackLink>
            <article>
              <ArticleHeader>
                <BlogThumbnail post={post} variant="hero" />
                <ByLine>
                  By <strong>{post.author}</strong> · {formatPostDate(post.publishedAt)}
                </ByLine>
              </ArticleHeader>
              <ArticleCard>
                <BlogBody body={post.body} />
              </ArticleCard>
              <BlogPostCta product={post.product} buddy={post.buddy} />
            </article>
          </Wrap>
          {related.length > 0 && (
            <section aria-label="Related posts">
              <RelatedTitle>Keep reading</RelatedTitle>
              <BlogGrid>
                {related.map((relatedPost) => (
                  <BlogCard key={relatedPost.slug} post={relatedPost} />
                ))}
              </BlogGrid>
            </section>
          )}
        </InnerContainer>
      </InnerSection>
      <Footer />
    </>
  );
};

const BlogPostPage: React.FC = () => {
  const { slug = '' } = useParams<{ slug: string }>();
  const post = getPostBySlug(slug);
  return post ? <BlogPostView post={post} /> : <NotFound />;
};

export default BlogPostPage;
